import React, { useState, useEffect } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import messages from '../../../messages'
import { Grid, Button, Typography } from '@material-ui/core'
import ErrorIcon from '@material-ui/icons/Error'
import { Field, Form, Formik, FormikProps } from 'formik'
import { isEmpty, cloneDeep } from 'lodash'
import { useRouter, genomicsRoute } from '@src/routes'
import {
  useCustomCrisprTotalValidationMutation,
  useCustomCrisprSaveToSessionMutation,
} from '@src/mutations/ConfiguratorTubesMutation.generated'
import {
  ConfiguratorProductInput,
  ListSubModel,
} from '@src/types/graphql-types'
import LiquidInputAdapter from '@src/components/LiquidInputAdapter'
import { GenomicsProperties, ActiveLinkType } from '../enum'
import * as yup from 'yup'
import { ConfigContext } from '../Genomics'
import CircleLoader from '@src/components/CircleLoader'
import { ConfiguratorValues } from '../../../utils/constants'
import SequenceField from './SequenceField'
import ResponsiveModal, { ModalSizes } from '@src/components/ResponsiveModal'
import {
  ResponsiveModalActions,
  ResponsiveModalBody,
} from '@src/components/ResponsiveModal/ResponsiveModal'

export interface CrisprItemFormValues {
  name: string
  geneId: string
  sequence: string
  geneSymbol: string
}

const loadInitialFormValues = (item) => ({
  name: item.name,
  geneId: item.geneId,
  sequence: item.sequence,
  geneSymbol: item.geneSymbol,
})
export interface ViewCrispr {
  dialogOpen: boolean
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>
  selectedItem?: ListSubModel
  selectedItemIndex: number
  productDetails: ConfiguratorProductInput
  isSequenceEntryPage: boolean
}
const fullValidatorForSchema = (schema) => (values) =>
  schema
    .validate(values, {
      abortEarly: false,
      strict: false,
    })
    .then(() => ({}))
    .catch(({ inner }) =>
      inner.reduce(
        (memo, { path, message }, index) => ({
          ...memo,
          [path]: (memo[path] || []).concat(
            <div style={{ marginBottom: 6 }}>
              {index > 0 && (
                <ErrorIcon
                  style={{
                    fontSize: 14,
                    marginLeft: '-18px',
                    marginRight: 4,
                  }}
                />
              )}
              {message}
            </div>
          ),
        }),
        {}
      )
    )

const getSpecialCharacter = (val: string): string => {
  let splChar = ''
  if (val) {
    val.split('').forEach((c) => {
      if (
        !c.match(/^[a-zA-Z0-9_#%&*().,:+.[\-\]/ ]*$/) &&
        !splChar.includes(c)
      ) {
        if (splChar === '') {
          splChar = c
        } else {
          splChar = splChar + ', ' + c
        }
      }
    })
  }
  return splChar
}

const loadValidationSchema = (intl) => {
  return yup.object().shape({
    name: yup
      .string()
      .trim()
      .required(intl.formatMessage(messages.CONF_REQUIRED_FIELD))
      .max(
        GenomicsProperties.NameMaxLength,
        intl.formatMessage(messages.CONF_ADD_INFO_MAX_VALIDATION_MSG) +
          GenomicsProperties.NameMaxLength
      )
      .test(
        'noSpecialCharacters',
        ({ value }) =>
          intl.formatMessage(
            messages.CONF_OLIGO_NAME_SPECIAL_CHARACTER_MASSAGE
          ) + getSpecialCharacter(value),
        (value) => !getSpecialCharacter(value)
      ),
    geneId: yup
      .string()
      .max(
        GenomicsProperties.GeneLength,
        intl.formatMessage(messages.CONF_ADD_INFO_MAX_VALIDATION_MSG) +
          GenomicsProperties.GeneLength
      )
      .test(
        'noSpecialCharacters',
        ({ value }) =>
          intl.formatMessage(messages.CONF_GENE_ID_SPECIAL_CHARACTER_MASSAGE) +
          getSpecialCharacter(value),
        (value) => !getSpecialCharacter(value)
      )
      .nullable(),
    geneSymbol: yup
      .string()
      .max(
        GenomicsProperties.GeneLength,
        intl.formatMessage(messages.CONF_ADD_INFO_MAX_VALIDATION_MSG) +
          GenomicsProperties.GeneLength
      )
      .test(
        'noSpecialCharacters',
        ({ value }) =>
          intl.formatMessage(
            messages.CONF_GENE_SYMBOL_SPECIAL_CHARACTER_MASSAGE
          ) + getSpecialCharacter(value),
        (value) => !getSpecialCharacter(value)
      )
      .nullable(),
  })
}

const ViewCrisprItem: React.FC<ViewCrispr> = (props) => {
  const intl = useIntl()
  const router = useRouter()
  const { product = '' } = router.query
  const {
    dialogOpen,
    setDialogOpen,
    selectedItemIndex,
    productDetails,
    isSequenceEntryPage,
  } = props
  const { state, dispatch } = React.useContext(ConfigContext)
  const { sessionData } = state
  const validationSchema = loadValidationSchema(intl)

  const initialFormValues = loadInitialFormValues(props.selectedItem)

  const [sequenceValidation, setSequenceValidation] = useState<string[]>([])
  const [customCrisprTotalValidationMutation, { loading: validationLoading }] =
    useCustomCrisprTotalValidationMutation()

  const [isSave, setIsSave] = useState(false)

  const [customCrisprSaveToSession, { loading: saveLoading }] =
    useCustomCrisprSaveToSessionMutation()

  const [tempSession, setTempSession] = useState<any>('')

  React.useEffect(
    () => {
      const newTempSession: any = cloneDeep(sessionData)
      setTempSession(newTempSession)
    },
    // TODO: need a test before we refactor these dependencies

    []
  )

  const handleClose = () => {
    setSequenceValidation([])
    sessionData[0].listSubModels[selectedItemIndex].sequence =
      tempSession[0].listSubModels[selectedItemIndex].sequence
    sessionData[0].listSubModels[selectedItemIndex].baseCount =
      tempSession[0].listSubModels[selectedItemIndex].sequence
        .split(' ')
        .join('').length
    sessionData[0].listSubModels[selectedItemIndex].name =
      tempSession[0].listSubModels[selectedItemIndex].name
    sessionData[0].listSubModels[selectedItemIndex].geneId =
      tempSession[0].listSubModels[selectedItemIndex].geneId
    sessionData[0].listSubModels[selectedItemIndex].geneSymbol =
      tempSession[0].listSubModels[selectedItemIndex].geneSymbol
    setDialogOpen(false)
  }

  const handleSave = async (values) => {
    sessionData[0].listSubModels[selectedItemIndex].name = values.name.trim()
    sessionData[0].listSubModels[selectedItemIndex].sequence = values.sequence
    sessionData[0].listSubModels[selectedItemIndex].geneId = values.geneId
    sessionData[0].listSubModels[selectedItemIndex].geneSymbol =
      values.geneSymbol
    if (isSequenceEntryPage) {
      sessionData[0].physicalMaterial = ''
      dispatch({ type: 'SET_STEP_NO', data: 0 })
    }
    setIsSave(true)
    sessionData[0].listSubModels[selectedItemIndex].isCurrentForm = false
    const response = await customCrisprTotalValidationMutation({
      variables: { input: sessionData, productInput: productDetails },
    })
    let validationResponse
    if (response?.data?.customCrisprTotalValidation)
      validationResponse = response?.data?.customCrisprTotalValidation[0]
    if (validationResponse && !validationResponse.serverError) {
      dispatch({
        type: 'SET_PAGE_ERROR',
        data: '',
      })
      const listSubModels = validationResponse.listSubModels

      if (listSubModels) {
        //Work in progress
        const errors: any[] = []
        const validationError =
          listSubModels[selectedItemIndex]?.listSubModelErrors

        if (validationError && validationError.length !== 0) {
          validationError.forEach((error) => {
            if (error && error.key === GenomicsProperties.SequenceKey) {
              errors.push({
                key: error?.key,
                value: error?.value,
              })
            }
            if (error && error.key === GenomicsProperties.Name) {
              errors.push({
                key: error?.key,
                value: error?.value,
              })
            }
          })
          dispatch({
            type: 'UPDATE_VIEW_ITEM_ERRORS',
            data: errors,
          })
        } else {
          sessionData[0].listSubModels[selectedItemIndex].listSubModelErrors =
            []
          dispatch({
            type: 'UPDATE_INPUT',
            data: [...sessionData],
          })
          await customCrisprSaveToSession({
            variables: { input: sessionData, productInput: productDetails },
          })

          dispatch({
            type: 'UPDATE_LIST_SUB_MODEL',
            data: [...sessionData[0].listSubModels],
          })
          dispatch({
            type: 'UPDATE_VIEW_ITEM_ERRORS',
            data: [],
          })
          setDialogOpen(false)
          if (
            router !== null &&
            router.query !== null &&
            router.query.cartid &&
            router.query.itemid
          ) {
            const url = `${genomicsRoute.index()}?product=${product}&activeLink=${
              ActiveLinkType.SequenceEntry
            }`
            router.push(url)
          }
        }
      }
    } else {
      dispatch({
        type: 'SET_PAGE_ERROR',
        data: ConfiguratorValues.InternalServerError,
      })
    }
  }

  const formikRef = React.useRef<FormikProps<any>>(null)
  useEffect(() => {
    formikRef?.current?.setFieldTouched('name', true)
  }, [])
  return (
    <Formik<CrisprItemFormValues>
      initialValues={initialFormValues}
      innerRef={formikRef}
      onSubmit={(values, actions) => {
        handleSave(values)
        setTimeout(() => {
          actions.setSubmitting(false)
        }, 1000)
      }}
      validate={fullValidatorForSchema(validationSchema)}
      validateOnChange
    >
      {(formikBag) => (
        <ResponsiveModal
          open={dialogOpen}
          size={ModalSizes.Large}
          onClose={handleClose}
          renderTitle={() => (
            <>
              <Typography variant="h2" component="h2">
                <FormattedMessage {...messages.CONF_CUSTOM_DESIGN_NAME} />{' '}
                {formikBag.values.name}
              </Typography>
              <div style={{ marginTop: '7px' }}>
                <Typography>
                  * <FormattedMessage {...messages.CONFIG_REQUIRED_FIELDS} />
                </Typography>
              </div>
            </>
          )}
        >
          <ResponsiveModalBody>
            <Form>
              <Grid container spacing={6}>
                <Grid item sm={7}>
                  <Field
                    name="name"
                    required
                    component={LiquidInputAdapter}
                    label={
                      <FormattedMessage {...messages.CONF_CRISPR_DESIGN_NAME} />
                    }
                    onChange={(e) => {
                      formikBag.setFieldTouched('name', true)
                      formikBag.setFieldValue('name', e.target.value)
                    }}
                    filled
                  />
                </Grid>
                <Grid item sm={7}>
                  <Field
                    name="geneId"
                    component={LiquidInputAdapter}
                    filled
                    label={
                      <FormattedMessage {...messages.CONF_CRISPR_GENE_ID} />
                    }
                    onChange={(e) => {
                      formikBag.setFieldTouched('geneId', true)
                      formikBag.setFieldValue('geneId', e.target.value)
                    }}
                  />
                </Grid>
                <Grid item sm={7}>
                  <Field
                    name="geneSymbol"
                    component={LiquidInputAdapter}
                    filled
                    label={
                      <FormattedMessage {...messages.CONF_CRISPR_GENE_SYMBOL} />
                    }
                    onChange={(e) => {
                      formikBag.setFieldTouched('geneSymbol', true)
                      formikBag.setFieldValue('geneSymbol', e.target.value)
                    }}
                  />
                </Grid>

                <SequenceField
                  formikBag={formikBag}
                  selectedItemIndex={selectedItemIndex}
                  isSequenceEntryPage={isSequenceEntryPage}
                  setIsSave={setIsSave}
                  initialSequence={props?.selectedItem?.sequence}
                  productDetails={props.productDetails}
                  setSequenceValidation={setSequenceValidation}
                  sequenceValidation={sequenceValidation}
                />
              </Grid>
              <ResponsiveModalActions>
                <Button
                  id="customCrispr-edit-popup-cancel-button"
                  onClick={handleClose}
                  color="primary"
                  variant="outlined"
                >
                  <FormattedMessage {...messages.CANCEL} />
                </Button>

                <Button
                  id="customCrispr-edit-popup-save-button"
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={
                    sequenceValidation.length > 0 ||
                    !isEmpty(formikBag.errors) ||
                    validationLoading
                  }
                >
                  <FormattedMessage {...messages.SAVE} />
                </Button>
                {isSave && (validationLoading || saveLoading) && (
                  <CircleLoader />
                )}
              </ResponsiveModalActions>
            </Form>
          </ResponsiveModalBody>
        </ResponsiveModal>
      )}
    </Formik>
  )
}
export default ViewCrisprItem
